import React, { FC } from "react";
import { Form, FormikConfig, FormikProvider, useFormik } from "formik";
import cn from "classnames";
import { toast } from "react-toastify";
import { Button } from "src/components/Button";
import { ButtonVariants } from "src/components/Button/types";
import { RenderFormFields } from "src/components/RenderFormFields";
import { ReactComponent as ArrowWhiteIcon } from "src/assets/icons/arrow-right-white-base.svg";
import { CONTACT_US_VALIDATION_SCHEMA } from "./constants/validation";
import { CONTACT_US_FORM_FIELDS } from "./constants/fields";
import { CORNER_STYLES } from "./constants/corner-styles";
import { ContactUsField, ContactUsValues } from "./types";
import { useSubmitContactFormMutation } from "src/redux/api";

const INITIAL_VALUES: ContactUsValues = {
  [ContactUsField.name]: "",
  [ContactUsField.phone]: "",
  [ContactUsField.email]: "",
  [ContactUsField.question]: "",
};

export const ContactUsForm: FC = () => {
  const [submitForm, { isLoading }] = useSubmitContactFormMutation();

  const formikConfig: FormikConfig<ContactUsValues> = {
    initialValues: INITIAL_VALUES,
    validationSchema: CONTACT_US_VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        await submitForm({
          name: values.name.trim(),
          phone: values.phone.trim(),
          email: values.email.trim(),
          question: values.question.trim(),
        }).unwrap();

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "formSubmissionSuccess",
        });

        toast.success("Заявку успішно надіслано!");
        resetForm();
      } catch (error: any) {
        const errorMessage = error?.data?.message || error?.data?.detail || "Помилка відправки форми. Спробуйте ще раз.";
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  };
  const formik = useFormik<ContactUsValues>(formikConfig);

  return (
    <FormikProvider value={formik}>
      <Form
        className={cn(
          "relative bg-blue-base flex justify-center items-center w-full p-5 flex-1 h-full rounded-l-2xl rounded-r-2xl lg:w-2/5 lg:rounded-l-none",
          ...CORNER_STYLES
        )}
      >
        <div className="flex flex-col gap-4 w-full lg:w-auto">
          <h2 className="text-24 lg:text-28 3xl:text-32 font-extrabold text-center text-green-base">
            Форма зворотнього зв’язку
          </h2>
          <div className="flex flex-col gap-4 flex-1 w-full">
            <RenderFormFields fields={CONTACT_US_FORM_FIELDS} />
          </div>

          <Button
            type="submit"
            className="w-full px-5 py-3 text-base md:px-6 md:py-4 3xl:text-24 3xl:px-8 3xl:py-6"
            variant={ButtonVariants.GREEN}
            isDisabled={isLoading || formik.isSubmitting}
          >
            <span className="flex-1 text-start">
              {isLoading || formik.isSubmitting ? "Відправка..." : "Надіслати заявку"}
            </span>
            {!(isLoading || formik.isSubmitting) && <ArrowWhiteIcon />}
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
};
